import React from "react";
import DataList from "components/datatable/DataList";
import useAxios from "hooks/useAxios";
import { MyConst } from "data/MyConst";
import { postgresDateToIndonesia } from "utils/MyUtils";
import { showEmpty } from "features/kms/system/SystemPageUtils";
import { SDMProfileDetail } from "features/kms/sdm/SDMKeolahragaanPage";
import { formatPersonName } from "features/kms/common/nameFormat";

function renderLineBreak(value) {
  return <span style={{ whiteSpace: "pre-line" }}>{showEmpty(value, "-")}</span>;
}

function formatDate(value) {
  return value ? postgresDateToIndonesia(value) || value : "";
}

function capaianAssetUrl(filename) {
  const clean = String(filename || "").trim();
  if (!clean) return "";
  return `${String(MyConst.URLASET || "").replace(/\/$/, "")}/capaian/${clean}`;
}

function parseCertificationItems(value) {
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function certificationLabel(item) {
  const title = [
    item?.nama_sertifikasi,
    item?.jenis_sertifikasi_name,
    item?.tingkat_name ? `Tk. ${item.tingkat_name}` : "",
  ].filter(Boolean).join(", ");
  const period = [formatDate(item?.berlaku_dari), formatDate(item?.berlaku_sampai)]
    .filter(Boolean)
    .join(" - ");
  return [title, period ? `masa berlaku ${period}` : ""].filter(Boolean).join(", ");
}

function renderCertificationList(_val, _col, item) {
  const items = parseCertificationItems(item?.sertifikasi_items);
  if (items.length < 1) return "-";
  return (
    <span>
      {items.map((certification, index) => {
        const label = certificationLabel(certification);
        const url = capaianAssetUrl(certification?.file_sertifikat);
        return (
          <React.Fragment key={`${label}-${index}`}>
            {url ? (
              <a href={url} target="_blank" rel="noreferrer">
                {label}
              </a>
            ) : (
              label
            )}
            {index < items.length - 1 ? <br /> : null}
          </React.Fragment>
        );
      })}
    </span>
  );
}

function CapaianSDMTable({ type = "prestasi" }) {
  const [peranResp] = useAxios({ method: "GET", url: "/capaian-sdm/peran-options", auth: true });
  const [caborResp] = useAxios({ method: "GET", url: "/capaian-sdm/cabor-options", auth: true });
  const peranOptions = Array.isArray(peranResp?.data) ? peranResp.data : [];
  const caborOptions = Array.isArray(caborResp?.data) ? caborResp.data : [];
  const isPrestasi = type === "prestasi";
  const title = isPrestasi ? "Poin Prestasi SDM" : "SDM Tersertifikasi";

  const coltype = {
    id_keanggotaan: { type: "hidden", name: "id_keanggotaan" },
    peran_name: { label: "Peran", width: 70, renderFunc: (val) => showEmpty(val) },
    nomor_induk: { label: "Nomor Induk", width: 80, align: "center", renderFunc: (val) => showEmpty(val) },
    nama_lengkap: {
      label: "Nama",
      width: 190,
      renderFunc: (_val, _col, item) => showEmpty(formatPersonName(item, ["nama_lengkap"])),
      renderExportFunc: (_val, _col, item) => showEmpty(formatPersonName(item, ["nama_lengkap"])),
    },
    jenis_kelamin_code: { label: "JK", width: 30, align: "center", renderFunc: (val) => showEmpty(val) },
    cabor_name: { label: "Cabor", width: 100, renderFunc: (val) => showEmpty(val) },
    poin_prestasi: { label: "Poin Prestasi", width: 100, align: "center", renderFunc: (val) => showEmpty(val, "0") },
    sertifikasi_text: {
      label: "Sertifikasi",
      width: 420,
      renderFunc: renderCertificationList,
      renderExportFunc: (val) => showEmpty(val),
    },
    action: { label: "Aksi", width: 55, align: "center" },
  };

  const columns = isPrestasi
    ? ["no", "peran_name", "nomor_induk", "nama_lengkap", "jenis_kelamin_code", "cabor_name", "poin_prestasi", "action"]
    : ["no", "peran_name", "nomor_induk", "nama_lengkap", "cabor_name", "sertifikasi_text", "action"];

  return (
    <DataList
      title={title}
      coltype={coltype}
      dlist={{
        url: isPrestasi ? "/capaian-sdm/prestasi" : "/capaian-sdm/sertifikasi",
        columns,
        filter: [
          { dtype: "q", name: "q", colsize: 4 },
          {
            dtype: "dropdown",
            name: "id_peran_keanggotaan",
            label: "Peran",
            colsize: 4,
            options: peranOptions,
            valueId: "id_peran_keanggotaan",
            labelId: "nama",
            placeholder: "-Semua Peran-",
          },
          {
            dtype: "dropdown",
            name: "id_cabor",
            label: "Cabor",
            colsize: 4,
            options: caborOptions,
            valueId: "id_cabor",
            labelId: "nama",
            placeholder: "-Semua Cabor-",
          },
        ],
        sort_by: ["peran_name", "nomor_induk", "nama_lengkap", "jenis_kelamin_code", "cabor_name", isPrestasi ? "poin_prestasi" : "sertifikasi_text"],
      }}
      detailconfig={{
        url: "/capaian-sdm/detail",
        cellid: "id_keanggotaan",
        title: "Detail SDM",
        size: "xl",
        tooltip: "Detail",
        icon: "fa-search",
        renderContent: (item) => (
          <SDMProfileDetail
            item={item}
            title={item?.peran_name || "SDM"}
            printId={`capaian-sdm-detail-${item?.id_keanggotaan || ""}`}
          />
        ),
      }}
      pdfconfig={{ orientation: "landscape" }}
      pdflist={{ title, columns: columns.filter((name) => name !== "action") }}
      xlslist={{ title, columns: columns.filter((name) => name !== "action") }}
    />
  );
}

export function CapaianPrestasiSDMPage() {
  return <CapaianSDMTable type="prestasi" />;
}

export function CapaianSertifikasiSDMPage() {
  return <CapaianSDMTable type="sertifikasi" />;
}

function CapaianPrestasiAggregateTable({ level = "induk" }) {
  const [peranResp] = useAxios({ method: "GET", url: "/capaian-sdm/peran-options", auth: true });
  const [caborResp] = useAxios({ method: "GET", url: "/capaian-sdm/cabor-options", auth: true });
  const peranOptions = Array.isArray(peranResp?.data) ? peranResp.data : [];
  const caborOptions = Array.isArray(caborResp?.data) ? caborResp.data : [];
  const isInduk = level === "induk";
  const isCabor = level === "cabor";
  const title = isInduk
    ? "Poin Prestasi Induk Organisasi"
    : isCabor
      ? "Poin Prestasi Cabor"
      : "Poin Prestasi Klub Olahraga";
  const url = isInduk
    ? "/capaian-sdm/prestasi-induk-organisasi"
    : isCabor
      ? "/capaian-sdm/prestasi-cabor"
      : "/capaian-sdm/prestasi-klub";

  const coltype = {
    peran_name: { label: "Peran", width: 70, renderFunc: (val) => showEmpty(val) },
    induk_organisasi_name: { label: "Induk Organisasi", width: 220, renderFunc: (val) => showEmpty(val) },
    cabor_name: { label: "Cabor", width: isCabor ? 220 : 120, renderFunc: (val) => showEmpty(val) },
    klub_name: { label: "Klub Olahraga", width: 220, renderFunc: (val) => showEmpty(val) },
    poin_prestasi: { label: "Poin Prestasi", width: 100, align: "center", renderFunc: (val) => showEmpty(val, "0") },
  };

  const columns = isInduk
    ? ["no", "peran_name", "induk_organisasi_name", "poin_prestasi"]
    : isCabor
      ? ["no", "peran_name", "cabor_name", "poin_prestasi"]
      : ["no", "peran_name", "cabor_name", "klub_name", "poin_prestasi"];

  return (
    <DataList
      title={title}
      coltype={coltype}
      dlist={{
        url,
        columns,
        filter: [
          { dtype: "q", name: "q", colsize: isInduk ? 6 : 4 },
          {
            dtype: "dropdown",
            name: "id_peran_keanggotaan",
            label: "Peran",
            colsize: isInduk ? 6 : 4,
            options: peranOptions,
            valueId: "id_peran_keanggotaan",
            labelId: "nama",
            placeholder: "-Semua Peran-",
          },
          !isInduk
            ? {
                dtype: "dropdown",
                name: "id_cabor",
                label: "Cabor",
                colsize: 4,
                options: caborOptions,
                valueId: "id_cabor",
                labelId: "nama",
                placeholder: "-Semua Cabor-",
              }
            : null,
        ].filter(Boolean),
        sort_by: columns.filter((name) => name !== "no"),
      }}
      pdfconfig={{ orientation: "landscape" }}
      pdflist={{ title, columns }}
      xlslist={{ title, columns }}
    />
  );
}

export function CapaianPrestasiIndukOrganisasiPage() {
  return <CapaianPrestasiAggregateTable level="induk" />;
}

export function CapaianPrestasiCaborPage() {
  return <CapaianPrestasiAggregateTable level="cabor" />;
}

export function CapaianPrestasiKlubPage() {
  return <CapaianPrestasiAggregateTable level="klub" />;
}
